// Global Instructions Rule Applied!
//
// The highlights history manifest (docs/AGENT_PROGRAMME_LP_HIGHLIGHTS_DESIGN.md
// Phase 5, moved under Configuration → Follower Agents → Backfill 2026-09-07):
// the dry-run preview and the enqueue result share one shape — per scope the
// instances and the sets to generate, per period the company atoms still
// missing (the cost driver; atoms are shared across funds, consolidated funds
// and LPs), the cost guidance, and the outcome alert. Read-only antd Tables;
// the surrounding form owns the controls and the commit button.
// 2026-10-28 (backlog 2.25-2.26): the atoms column counts only what the runs
// will pay for (live sets freeze free; under force approved atoms are
// reused), and under "Regenerate existing sets" the view lists the versions a
// person signed off or edited by hand that the runs will supersede. The two
// regenerate switches live here too (HighlightHistoryRegenerateControls), so
// their wording sits next to the manifest that explains it.
import React from 'react'
import { Alert, Switch, Table, Tag, Tooltip } from 'antd'
import dayjs from 'dayjs'
import type { HighlightBenchManifest, HighlightHistoryManifest, HighlightHistorySubject } from '../../../state_manager/data_models/period_highlights_model.ts'

const SCOPE_LABEL: Record<string, string> = { fund: 'Funds', fund_group: 'Consolidated funds', lp_account: 'LPs' }
const SUBJECT_LABEL: Record<string, string> = { fund: 'Fund', fund_group: 'Consolidated fund', lp_account: 'LP' }

/** The subjects a preview was narrowed to (the Backfill form's multi-select,
 *  2026-10-29; null = the fleet): their names in request order, and which of
 *  them has no provisioned tracker instance to run. */
const SubjectsLine: React.FC<{ subjects: HighlightHistorySubject[] | null | undefined }> = ({ subjects }) => {
  if (!subjects || subjects.length === 0) return null
  const kind = subjects[0].scope_type
  const label = (subjects.length === 1 ? SUBJECT_LABEL[kind] : SCOPE_LABEL[kind]) ?? kind
  const missing = subjects.filter((s) => s.instances === 0).map((s) => s.name ?? s.id)
  return (
    <div className='text-xs text-gray-600' data-testid='history-subjects'>
      {label}:{' '}
      {subjects.map((s, i) => (
        <React.Fragment key={s.id}>{i > 0 && ', '}<b>{s.name ?? s.id}</b></React.Fragment>
      ))}
      {missing.length > 0 && (
        <span className='text-amber-700'>
          {' '}— no provisioned tracker instance for {missing.join(', ')}; provision {missing.length === 1 ? 'it' : 'them'} under Instances below, then preview again.
        </span>
      )}
    </div>
  )
}
const money = (v: number | null | undefined) => `$${(v ?? 0).toFixed(2)}`
const period = (s: string, e: string) => `${dayjs(s).format('MM/DD/YYYY')} → ${dayjs(e).format('MM/DD/YYYY')}`

interface HighlightHistoryManifestViewProps {
  manifest: HighlightHistoryManifest
}

/** What the enqueue did, as a sentence: new rows first, then the merges /
 *  deferrals that produced NO new row, then any enqueue errors. */
export const describeHistoryEnqueue = (m: HighlightHistoryManifest): string => {
  const isNew = m.enqueued_new ?? m.enqueued
  const merged = m.merged ?? 0
  const deferred = m.deferred ?? 0
  const errors = m.enqueue_errors ?? 0
  const parts = [`${isNew} new run${isNew === 1 ? '' : 's'} queued`]
  if (merged) parts.push(`${merged} merged into a run already queued for the same period`)
  if (deferred) parts.push(`${deferred} parked behind a run already queued for another period`)
  if (errors) parts.push(`${errors} failed to enqueue`)
  return parts.join(', ')
}

/** The reviewed versions a forced run will supersede, as the sentence the
 *  commit Popconfirm and the manifest alert share; null when there are none. */
export const describeHistoryReplaces = (m: HighlightHistoryManifest): string | null => {
  const r = m.replaces
  if (!r.versions) return null
  const kinds = [r.signed_off ? `${r.signed_off} signed off` : '', r.edited ? `${r.edited} edited by hand` : ''].filter(Boolean).join(', ')
  return `${r.versions} version${r.versions === 1 ? '' : 's'} a person reviewed (${kinds}) will be replaced. ` +
    `Each run lands a new version, approved by policy and not reviewed, so ${r.versions === 1 ? 'it needs' : 'they need'} signing off again; ` +
    `the replaced version stays in the set's history as superseded.`
}

interface HighlightHistoryRegenerateControlsProps {
  force: boolean
  keepReviewed: boolean
  onForceChange: (value: boolean) => void
  onKeepReviewedChange: (value: boolean) => void
}

/** The Backfill form's regenerate switches. "Regenerate existing sets"
 *  re-runs the scope's set; company atoms that are already approved are
 *  reused (a forced tracker fans out only the members with no approved
 *  atom). The second switch appears under force and keeps the versions a
 *  person signed off or edited by hand. */
export const HighlightHistoryRegenerateControls: React.FC<HighlightHistoryRegenerateControlsProps> = ({
  force, keepReviewed, onForceChange, onKeepReviewedChange
}) => (
  <>
    <label className='flex items-center gap-2 pb-1'>
      <Switch size='small' checked={force} onChange={onForceChange} />
      <Tooltip title='Redo the sets of scopes and quarters that already have one. Company atoms that are already approved are reused, not regenerated; members with no approved atom get one (their live sets are not frozen under this switch).'>
        <span className='text-xs text-gray-500'>Regenerate existing sets (company atoms are reused)</span>
      </Tooltip>
    </label>
    {force && (
      <label className='flex items-center gap-2 pb-1'>
        <Switch size='small' checked={keepReviewed} onChange={onKeepReviewedChange} />
        <Tooltip title='Leave the scopes and quarters whose current version a person signed off or edited by hand; regenerate the rest.'>
          <span className='text-xs text-gray-500'>Keep signed-off and hand-edited versions</span>
        </Tooltip>
      </label>
    )}
  </>
)

const REVIEW_TAG: Record<string, { color: string; label: string }> = {
  signed_off: { color: 'green', label: 'Signed off' },
  edited_since_sign_off: { color: 'gold', label: 'Edited since sign-off' }
}

export const HighlightHistoryManifestView: React.FC<HighlightHistoryManifestViewProps> = ({ manifest }) => {
  const done = !manifest.dry_run
  const noNewRows = done && (manifest.enqueued_new ?? manifest.enqueued) === 0
  const replaced = describeHistoryReplaces(manifest)
  return (
    <div data-component='HighlightHistoryManifest' className='flex flex-col gap-3 text-[13px]'>
      <SubjectsLine subjects={manifest.subjects} />
      <Table
        size='small'
        rowKey='scope_type'
        pagination={false}
        dataSource={manifest.scopes}
        columns={[
          { title: 'Scope', dataIndex: 'scope_type', render: (v: string) => SCOPE_LABEL[v] ?? v },
          { title: 'Instances', dataIndex: 'instances', width: 90, align: 'right' as const },
          { title: 'Quarters', key: 'q', width: 260, render: (_: unknown, r) => r.periods.length ? `${period(r.periods[0].period_start, r.periods[0].period_end).split(' → ')[0]} … ${dayjs(r.periods[r.periods.length - 1].period_end).format('MM/DD/YYYY')}` : '—' },
          { title: 'Sets to generate', dataIndex: 'sets_to_generate', width: 130, align: 'right' as const },
          { title: 'Already have a set', dataIndex: 'sets_skipped', width: 140, align: 'right' as const, render: (v: number) => <span className='text-gray-400'>{v}</span> }
        ]}
      />
      <Table
        size='small'
        rowKey={(r) => `${r.period_start}-${r.period_end}`}
        pagination={false}
        dataSource={manifest.atoms}
        columns={[
          { title: 'Period', key: 'p', render: (_: unknown, r) => period(r.period_start, r.period_end) },
          { title: 'Companies', dataIndex: 'companies', width: 100, align: 'right' as const },
          { title: 'Atoms approved', dataIndex: 'atoms_found', width: 130, align: 'right' as const, render: (v: number) => <span className='text-gray-500'>{v}</span> },
          ...(manifest.force ? [] : [{ title: 'Live (frozen free)', dataIndex: 'atoms_live', width: 140, align: 'right' as const, render: (v: number) => <span className='text-gray-500'>{v}</span> }]),
          { title: 'Atoms to generate', dataIndex: 'atoms_missing', width: 140, align: 'right' as const, render: (v: number) => v > 0 ? <Tag color='gold'>{v}</Tag> : <Tag color='green'>0</Tag> }
        ]}
      />
      <div className='text-xs text-gray-600'>
        Estimate: {manifest.atoms_missing} atom{manifest.atoms_missing === 1 ? '' : 's'} × {money(manifest.estimate.atom_cost_usd)} + {manifest.sets_to_generate} set{manifest.sets_to_generate === 1 ? '' : 's'} × {money(manifest.estimate.set_cost_usd)} ≈ <b>{money(manifest.estimate.total_usd)}</b>
        {' '}({manifest.force
          ? 'approved atoms are reused; under Regenerate the live sets are not frozen, so a company with only a live set gets an atom run'
          : 'companies with a live set freeze into atoms at no cost'}; guidance only, and the daily cap defers runs rather than failing them).
      </div>
      {replaced && (
        <>
          <Alert type='warning' showIcon message={replaced} />
          <Table
            size='small'
            rowKey={(r) => r.set_id}
            pagination={false}
            dataSource={manifest.replaces.rows}
            columns={[
              { title: SUBJECT_LABEL[manifest.replaces.rows[0]?.scope_type ?? ''] ?? 'Scope', key: 'n', render: (_: unknown, r) => r.name ?? r.scope_id },
              { title: 'Period', key: 'p', width: 210, render: (_: unknown, r) => period(r.period_start, r.period_end) },
              { title: 'Version', key: 'v', width: 90, render: (_: unknown, r) => `v${r.version}${r.status === 'draft' ? ' draft' : ''}` },
              {
                title: 'Review', key: 'rs', width: 170,
                render: (_: unknown, r) => r.review_status && REVIEW_TAG[r.review_status]
                  ? <Tag color={REVIEW_TAG[r.review_status].color}>{REVIEW_TAG[r.review_status].label}</Tag>
                  : <span className='text-gray-400'>—</span>
              },
              {
                title: 'Hand edits', key: 'he', width: 200,
                render: (_: unknown, r) => [
                  r.items_edited ? `${r.items_edited} item${r.items_edited === 1 ? '' : 's'}` : '',
                  r.narrative_edited ? 'narrative' : ''
                ].filter(Boolean).join(', ') || <span className='text-gray-400'>—</span>
              }
            ]}
          />
          {manifest.replaces.truncated && <div className='text-xs text-gray-500'>Showing the first {manifest.replaces.rows.length}.</div>}
        </>
      )}
      {done && (
        <Alert
          type={noNewRows ? 'warning' : 'success'}
          showIcon
          message={`${describeHistoryEnqueue(manifest)} — the first period per instance runs now, the rest follow one after another. Watch Runs below and the Status column on Reporting → Period Highlights.`}
          description={noNewRows
            ? 'No new run was created: every target instance already holds a queued run (typically a tracker polling for its member atoms), and the request was folded into it. Nothing new will appear in Runs until that run finishes or is cancelled.'
            : undefined}
        />
      )}
      {!done && manifest.sets_to_generate === 0 && (
        <Alert
          type='info'
          showIcon
          message={manifest.keep_reviewed
            ? 'Nothing to generate — every set in scope was signed off or edited by hand, and “Keep signed-off and hand-edited versions” leaves those alone.'
            : 'Nothing to generate — every instance in scope already has a set for these quarters. Switch on “Regenerate existing sets” to redo them.'}
        />
      )}
    </div>
  )
}

// ── the runners-up backfill (2026-10-29) ─────────────────────────────────────

/** What the runners-up enqueue did, as a sentence (the history idiom). */
export const describeBenchEnqueue = (m: HighlightBenchManifest): string => {
  const isNew = m.enqueued_new ?? m.enqueued
  const merged = m.merged ?? 0
  const deferred = m.deferred ?? 0
  const errors = m.enqueue_errors ?? 0
  const parts = [`${isNew} runners-up pass${isNew === 1 ? '' : 'es'} queued`]
  if (merged) parts.push(`${merged} merged into a pass already queued for the same period`)
  if (deferred) parts.push(`${deferred} parked behind a run already queued for the scope`)
  if (errors) parts.push(`${errors} failed to enqueue`)
  return parts.join(', ')
}

/** The runners-up backfill preview / result: per scope the instances and
 *  the current approved sets a model-free pass will add the bench to; a key
 *  with only a draft, or none, is skipped (the finalise writes its own
 *  bench). No cost line — there is no model call. */
export const HighlightBenchManifestView: React.FC<{ manifest: HighlightBenchManifest }> = ({ manifest }) => {
  const done = !manifest.dry_run
  const noNewRows = done && (manifest.enqueued_new ?? manifest.enqueued) === 0
  return (
    <div data-component='HighlightBenchManifest' className='flex flex-col gap-3 text-[13px]'>
      <SubjectsLine subjects={manifest.subjects} />
      <Table
        size='small'
        rowKey='scope_type'
        pagination={false}
        dataSource={manifest.scopes}
        columns={[
          { title: 'Scope', dataIndex: 'scope_type', render: (v: string) => SCOPE_LABEL[v] ?? v },
          { title: 'Instances', dataIndex: 'instances', width: 90, align: 'right' as const },
          { title: 'Quarters', key: 'q', width: 260, render: (_: unknown, r) => r.periods.length ? `${period(r.periods[0].period_start, r.periods[0].period_end).split(' → ')[0]} … ${dayjs(r.periods[r.periods.length - 1].period_end).format('MM/DD/YYYY')}` : '—' },
          { title: 'Sets to update', dataIndex: 'sets_to_update', width: 130, align: 'right' as const, render: (v: number) => v > 0 ? <Tag color='gold'>{v}</Tag> : <Tag color='green'>0</Tag> },
          { title: 'No current approved set', dataIndex: 'sets_skipped', width: 180, align: 'right' as const, render: (v: number) => <span className='text-gray-400'>{v}</span> }
        ]}
      />
      <div className='text-xs text-gray-600'>
        Each pass appends up to <b>{manifest.bench_items}</b> runners-up to the set, in the company atoms’ own wording, as excluded rows after the selected highlights.
        No model call, no cost; nothing a report prints changes, so sign-offs stand.
        {manifest.bench_items === 0 && <span className='text-amber-700'> cfg period_highlights.bench_items is 0 — nothing will be added.</span>}
      </div>
      {done && (
        <Alert
          type={noNewRows ? 'warning' : 'success'}
          showIcon
          message={`${describeBenchEnqueue(manifest)} — they run on the queue tick, staggered per instance. Watch Runs below; each set form shows its runners-up under “Also considered”.`}
          description={noNewRows
            ? 'No new run was created: every target instance already holds a queued run, and the pass was parked behind it (it re-emits once that run finishes).'
            : undefined}
        />
      )}
      {!done && manifest.sets_to_update === 0 && (
        <Alert type='info' showIcon message='Nothing to update — no instance in scope has a current approved set for these quarters. Generate the sets first (the finalise stores its own runners-up).' />
      )}
    </div>
  )
}

export default HighlightHistoryManifestView
